import {
  BENEFIT_LIFE,
  COLUMNS,
  GENDERS,
  PROCESSED_CELL_STATUS,
  RELATIONSHIPS,
} from "./constants";
import { isValidDate, isValidNumber } from "./lib/utils";
import {
  ColumnName,
  ProcessedCell,
  ProcessedRow,
  ValidationContext,
  ValidationFunction,
  ValueOf,
} from "./types";
import { trimLowerCase } from "./utils";

const validateEmptyDefault = validateFactory([validateNotEmpty, validateValue]);
const validateWaivedTiers = validateFactory([
  validateWaived,
  validateTiers,
  invalidateValue,
]);
const validateWaivedCovered = validateFactory([
  validateWaived,
  validateCovered,
  invalidateValue,
]);
const validateWaivedNumber = validateFactory([
  validateWaived,
  validateNumber,
  invalidateValue,
]);

const VALIDATORS: {
  [key in ColumnName]: {
    validate: ValidationFunction;
    dependencies?: ColumnName[];
  };
} = {
  relationship: {
    validate: validateFactory([
      validateNotEmpty,
      validateRelationship,
      invalidateValue,
    ]),
  },
  gender: {
    validate: validateFactory([
      validateNotEmpty,
      validateGender,
      invalidateValue,
    ]),
  },
  "date of birth": {
    validate: validateFactory([
      validateNotEmpty,
      validateDate,
      invalidateValue,
    ]),
  },
  zip: {
    validate: validateFactory([validateZipCode, invalidateValue]),
  },
  "employment type": {
    validate: validateEmptyDefault,
  },
  "employment status": {
    validate: validateFactory([
      validateNotEmpty,
      validateEmploymentStatus,
      invalidateValue,
    ]),
  },
  occupation: {
    validate: validateEmptyDefault,
  },
  "eligibility class": {
    validate: validateFactory([validateEligibilityClass, invalidateValue]),
  },
  "annual salary": {
    validate: validateFactory([
      validateNotEmpty,
      validateNumberGreaterThan0,
      invalidateValue,
    ]),
  },
  "dental plan coverage": {
    validate: validateWaivedTiers,
  },
  "dental plan name": {
    validate: validateFactory([validateWaived, invalidateValue]),
  },
  "vision plan coverage": {
    validate: validateWaivedTiers,
  },
  "vision plan name": {
    validate: validateFactory([validateWaived, invalidateValue]),
  },
  "basic life/ad&d ee coverage": {
    validate: validateWaivedCovered,
  },
  "std election": {
    validate: validateWaivedCovered,
  },
  "ltd election": {
    validate: validateWaivedCovered,
  },
  "vol life ee coverage": {
    validate: validateWaivedNumber,
  },
  "vol ad&d ee coverage": {
    validate: validateWaivedNumber,
  },
  benefit: {
    validate: validateFactory([validateBenefit, invalidateValue]),
  },
  "plan name": {
    validate: validateFactory([validatePlanName, invalidateValue]),
    dependencies: [COLUMNS.BENEFIT],
  },
  "coverage tier": {
    validate: validateFactory([validateCoverageTier, invalidateValue]),
    dependencies: [COLUMNS.BENEFIT, COLUMNS.PLAN_NAME],
  },
};

function validateFactory(
  validators: Array<
    (
      value: string,
      processedRow: ProcessedRow,
      context?: ValidationContext,
    ) => ProcessedCell | null
  >,
) {
  return function (
    value: string,
    processedRow: ProcessedRow,
    context?: ValidationContext,
  ): ProcessedCell {
    for (const validator of validators) {
      const result = validator(value, processedRow, context);
      if (result) return result;
    }

    throw new Error("No validator found");
  };
}

function validateValue(original: string): ProcessedCell {
  return { status: PROCESSED_CELL_STATUS.VALID, original, processed: original };
}
function invalidateValue(
  original: string,
  _?: ProcessedRow,
  context?: ValidationContext,
): ProcessedCell {
  return {
    status: PROCESSED_CELL_STATUS.INVALID_VALUE,
    original,
    errorCode: context?.errorCode,
    processed: null,
  };
}
function validateNotEmpty(original: string): ProcessedCell | null {
  if (original === "") {
    return invalidateValue(original);
  }

  return null;
}
function validateRelationship(original: string): ProcessedCell | null {
  const processed = RELATIONSHIP_MAPPING[trimLowerCase(original)] ?? null;
  if (processed) {
    return { status: "valid", original, processed };
  }

  return null;
}
function validateGender(original: string): ProcessedCell | null {
  const processed = GENDER_MAPPING[trimLowerCase(original)] ?? null;
  if (processed) {
    return { status: "valid", original, processed };
  }

  return null;
}
function validateDate(original: string): ProcessedCell | null {
  const [valid, processed] = isValidDate(original);
  if (valid) {
    return { status: "valid", original, processed };
  }

  return null;
}
function validateEmploymentStatus(original: string): ProcessedCell | null {
  const processed = EMPLOYMENT_STATUS_MAPPING[trimLowerCase(original)] ?? null;
  if (processed) {
    return { status: "valid", original, processed };
  }

  return null;
}
function validateNumber(original: string): ProcessedCell | null {
  const [valid, processed] = isValidNumber(original);
  if (valid) {
    return { status: "valid", original, processed };
  }

  return null;
}
function validateNumberGreaterThan0(original: string): ProcessedCell | null {
  const [valid, processed] = isValidNumber(original);
  if (valid && processed > 0) {
    return { status: "valid", original, processed };
  }

  return null;
}
function validateWaived(original: string): ProcessedCell | null {
  if (WAIVED_VALUES.includes(trimLowerCase(original))) {
    return { status: "valid", original, processed: "Waived" };
  }

  return null;
}
function validateTiers(original: string): ProcessedCell | null {
  const processed = TIERS_MAPPING[trimLowerCase(original)] ?? null;
  if (processed) {
    return { status: "valid", original, processed };
  }

  return null;
}
function validateCovered(original: string): ProcessedCell | null {
  const isCovered = COVERED_VALUES.includes(trimLowerCase(original));
  if (isCovered) {
    return { status: "valid", original, processed: "Covered" };
  }
  const [valid] = isValidNumber(original);
  if (valid) {
    return { status: "valid", original, processed: "Covered" };
  }

  return null;
}

function validateZipCode(original: string): ProcessedCell | null {
  const zipCodeRegex = /^\d{5}(-\d{4})?$/;
  if (zipCodeRegex.test(original.trim())) {
    return { status: "valid", original, processed: original.slice(0, 5) };
  }

  return null;
}

function validateEligibilityClass(
  original: string,
  _: ProcessedRow,
  context?: ValidationContext,
): ProcessedCell | null {
  const classes = context?.classes || [];
  const index = classes.indexOf(original.toLocaleLowerCase());
  if (index !== -1) {
    return { status: "valid", original, processed: `Class ${index + 1}` };
  }

  return null;
}

function validateBenefit(original: string): ProcessedCell | null {
  const multipleBenefitsError = invalidateValue(original, undefined, {
    errorCode: "multiple-benefits",
  });

  const valueOccurrences = removeOccurrences(
    trimLowerCase(original),
    UNIQUE_BENEFIT_VALUES,
  );
  if (valueOccurrences.occurrences.length > 1) {
    return multipleBenefitsError;
  }

  const voluntaryADDOccurrences = removeOccurrences(
    valueOccurrences.newValue,
    VOLUNTARY_ADD_BENEFIT_VALUES,
  );
  if (voluntaryADDOccurrences.occurrences.length > 1) {
    return multipleBenefitsError;
  }

  const voluntaryLifeOccurrences = removeOccurrences(
    voluntaryADDOccurrences.newValue,
    VOLUNTARY_LIFE_BENEFIT_VALUES,
  );
  if (voluntaryLifeOccurrences.occurrences.length > 1) {
    return multipleBenefitsError;
  }

  if (
    valueOccurrences.occurrences.length === 1 &&
    (voluntaryLifeOccurrences.occurrences.length === 1 ||
      voluntaryADDOccurrences.occurrences.length === 1)
  ) {
    return multipleBenefitsError;
  }

  const occurrences = [
    ...valueOccurrences.occurrences,
    ...voluntaryLifeOccurrences.occurrences,
    ...voluntaryADDOccurrences.occurrences,
  ];

  let processed: string | null = null;
  if (occurrences.length > 0) {
    processed = occurrences
      .map((b) => BENEFIT_COVERAGE_TIER_MAPPING[b])
      .join("|");
  } else {
    const lifeOccurrences = removeOccurrences(
      voluntaryLifeOccurrences.newValue,
      [BENEFIT_LIFE],
    );
    if (lifeOccurrences.occurrences.length > 0) {
      processed = BENEFIT_LIFE;
    } else {
      return null;
    }
  }
  return { status: "valid", original, processed };
}

function validatePlanName(
  original: string,
  processedRow: ProcessedRow,
  context?: ValidationContext,
): ProcessedCell | null {
  const benefitCell = processedRow[COLUMNS.BENEFIT];

  // if the benefit cell is not valid, no need to validate plan name
  if (!benefitCell || benefitCell.status !== "valid") {
    return validateValue(original);
  }

  const benefit = benefitCell.processed as string;
  const column = BENEFIT_PLAN_NAME_MAPPING[benefit];

  // if the benefit maps to a column, run plan name value against that column's validator
  if (column) {
    let processedValue: ProcessedCell;
    const customColumn = context?.customColumnMap?.[column];
    if (customColumn) {
      const cellValue = customColumn.getCustomValue(
        { text: original },
        processedRow,
      );
      processedValue = {
        status: "valid",
        original,
        processed: cellValue.text,
      };
    } else {
      processedValue = VALIDATORS[column]?.validate(original, processedRow);
    }
    if (processedValue.status === "valid") {
      processedRow[column] = processedValue;
      return validateValue(original);
    }
    // if benefit is life, run plan name value against benefit validator
  } else if (benefit === BENEFIT_LIFE) {
    let benefitValue = validateBenefit(original);
    if (benefitValue === null) {
      benefitValue = invalidateValue(
        processedRow[COLUMNS.BENEFIT]?.original ?? "",
        undefined,
        {
          errorCode: "unmapped-life",
        },
      );
    }
    processedRow[COLUMNS.BENEFIT] = {
      ...processedRow[COLUMNS.BENEFIT],
      ...benefitValue,
    };
    return validateValue(original);
    // if benefit value is not mapped to a column, no need to validate plan name
  } else {
    return validateValue(original);
  }

  return null;
}

function validateCoverageTier(
  original: string,
  processedRow: ProcessedRow,
): ProcessedCell | null {
  const benefitCell = processedRow["benefit"];

  // if the benefit cell is not valid, no need to validate coverage tier
  if (!benefitCell || benefitCell.status !== "valid") {
    return validateValue(original);
  }

  if (typeof benefitCell.processed !== "string") {
    return invalidateValue(original);
  }

  let isValid = true;
  const columns = benefitCell.processed.split("|") as ColumnName[];
  for (const column of columns) {
    const processedValue = VALIDATORS[column]?.validate(original, processedRow);
    if (processedValue?.status === "valid") {
      processedRow[column] = processedValue;
    } else {
      isValid = false;
      break;
    }
  }

  if (isValid) {
    return validateValue(original);
  }

  return null;
}

function removeOccurrences(
  value: string,
  valuesToCheck: string[],
): { occurrences: string[]; newValue: string } {
  const occurrences: string[] = [];

  let newValue = value;
  for (const value of valuesToCheck) {
    const index = newValue.indexOf(value);
    if (index !== -1) {
      occurrences.push(value);
      newValue = trimLowerCase(
        newValue.slice(0, index) + newValue.slice(index + value.length),
      );
    }
  }
  return { occurrences, newValue };
}

const RELATIONSHIP_MAPPING: Record<string, ValueOf<typeof RELATIONSHIPS>> = {
  employee: RELATIONSHIPS.EMPLOYEE,
  ee: RELATIONSHIPS.EMPLOYEE,
  self: RELATIONSHIPS.EMPLOYEE,
  member: RELATIONSHIPS.EMPLOYEE,
  spouse: RELATIONSHIPS.SPOUSE,
  sp: RELATIONSHIPS.SPOUSE,
  "domestic partner": RELATIONSHIPS.DOMESTIC_PARTNER,
  dp: RELATIONSHIPS.DOMESTIC_PARTNER,
  partner: RELATIONSHIPS.DOMESTIC_PARTNER,
  child: RELATIONSHIPS.CHILD,
  children: RELATIONSHIPS.CHILD,
  ch: RELATIONSHIPS.CHILD,
  "legal dependent": RELATIONSHIPS.LEGAL_DEPENDENT,
  dependent: RELATIONSHIPS.LEGAL_DEPENDENT,
};

const GENDER_MAPPING: Record<string, ValueOf<typeof GENDERS>> = {
  male: GENDERS.MALE,
  m: GENDERS.MALE,
  female: GENDERS.FEMALE,
  f: GENDERS.FEMALE,
  "not disclosed": GENDERS.NOT_DISCLOSED,
  "not specified": GENDERS.NOT_DISCLOSED,
  "non binary": GENDERS.NON_BINARY,
  "non-binary": GENDERS.NON_BINARY,
  nb: GENDERS.NON_BINARY,
};
const EMPLOYMENT_STATUS_MAPPING: Record<string, string> = {
  active: "Active",
  a: "Active",
  "a - active": "Active",
  retired: "Retired",
  retire: "Retired",
  r: "Retired",
  "r- retiree": "Retired",
  retiree: "Retired",
  cobra: "Cobra",
  c: "Cobra",
  "c - cobra": "Cobra",
  leave: "Leave",
  "on leave": "Leave",
  l: "Leave",
  "l - leave": "Leave",
  terminated: "Terminated",
  term: "Terminated",
  t: "Terminated",
  "t - terminated": "Terminated",
};
const TIERS_MAPPING: Record<string, string> = {
  "ee only": "EE Only",
  ee: "EE Only",
  "employee only": "EE Only",
  employee: "EE Only",
  single: "EE Only",
  eo: "EE Only",
  "ee + sp": "EE + SP",
  "ee+sp": "EE + SP",
  "ee + spouse": "EE + SP",
  "employee + spouse": "EE + SP",
  "employee + sp": "EE + SP",
  "employee/spouse": "EE + SP",
  esp: "EE + SP",
  "employee and spouse": "EE + SP",
  es: "EE + SP",
  "ee/sp": "EE + SP",
  "ee + dp": "EE + DP",
  "ee+dp": "EE + DP",
  "ee + domestic partner": "EE + DP",
  "employee + domestic partner": "EE + DP",
  "employee + dp": "EE + DP",
  edp: "EE + DP",
  "ee/dp": "EE + DP",
  "ee + ch": "EE + CH",
  "ee+ch": "EE + CH",
  "ee + child": "EE + CH",
  "ee + child(ren)": "EE + CH",
  "ee + children": "EE + CH",
  "employee + child": "EE + CH",
  "employee + child(ren)": "EE + CH",
  "employee + children": "EE + CH",
  "employee + ch": "EE + CH",
  "employee/child": "EE + CH",
  ech: "EE + CH",
  "employee and child": "EE + CH",
  ec: "EE + CH",
  "ee/ch": "EE + CH",
  "ee + fam": "EE + Fam",
  "ee+fam": "EE + Fam",
  "ee + family": "EE + Fam",
  "ee+family": "EE + Fam",
  "employee + family": "EE + Fam",
  "employee + fam": "EE + Fam",
  "employee + spouse + 1 child": "EE + Fam",
  "employee + spouse + 2 children": "EE + Fam",
  "employee/family": "EE + Fam",
  fam: "EE + Fam",
  "employee and family": "EE + Fam",
  f: "EE + Fam",
  ef: "EE + Fam",
  family: "EE + Fam",
  "ee + 1": "EE + 1",
  "ee+1": "EE + 1",
  "ee + 2": "EE + 2",
  "ee+2": "EE + 2",
  "spouse only": "SP",
  "child only": "CH",
  "spouse + dependent": "SP + DP",
  "spouse + children": "SP + CH",
  "not disclosed": "Not Disclosed",
};
const WAIVED_VALUES = [
  "waived",
  "not covered",
  "waive",
  "no",
  "0",
  "",
  "n",
  "wv",
  "waiver",
  "w",
];
const COVERED_VALUES = ["covered", "included", "x", "yes", "y"];

const UNIQUE_BENEFIT_VALUES = [
  "employer provided life",
  "short term disability",
  "short-term disability",
  "long term disability",
  "long-term disability",
  "company-paid life",
  "employer-paid life",
  "employer life",
  "basic life",
  "dental",
  "vision",
  "std",
  "ltd",
  "den",
  "vis",
];

const VOLUNTARY_ADD_BENEFIT_VALUES = [
  "voluntary life and ad&d",
  "voluntary life with ad&d",
  "voluntary life w/ ad&d",
  "voluntary ad&d",
  "ad&d vol ee",
  "vol ad&d",
];

const VOLUNTARY_LIFE_BENEFIT_VALUES = [
  "voluntary employee life",
  "employee-paid life",
  "voluntary life",
  "life vol ee",
  "vol life",
];

const BENEFIT_COVERAGE_TIER_MAPPING: Record<string, string> = {
  "short term disability": COLUMNS.STD_ELECTION,
  "short-term disability": COLUMNS.STD_ELECTION,
  std: COLUMNS.STD_ELECTION,
  "long term disability": COLUMNS.LTD_ELECTION,
  "long-term disability": COLUMNS.LTD_ELECTION,
  ltd: COLUMNS.LTD_ELECTION,
  "basic life": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "employer provided life": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "employer life": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "company-paid life": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "employer-paid life": COLUMNS.BASIC_LIFE_ADD_EE_COVERAGE,
  "voluntary life": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "voluntary employee life": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "employee-paid life": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "life vol ee": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "vol life": COLUMNS.VOLUNTARY_LIFE_EE_COVERAGE,
  "voluntary ad&d": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "voluntary life and ad&d": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "voluntary life with ad&d": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "voluntary life w/ ad&d": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "ad&d vol ee": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  "vol ad&d": COLUMNS.VOLUNTARY_ADD_EE_COVERAGE,
  den: COLUMNS.DENTAL_PLAN_COVERAGE,
  dental: COLUMNS.DENTAL_PLAN_COVERAGE,
  vis: COLUMNS.VISION_PLAN_COVERAGE,
  vision: COLUMNS.VISION_PLAN_COVERAGE,
};

const BENEFIT_PLAN_NAME_MAPPING: Record<string, ColumnName> = {
  [COLUMNS.DENTAL_PLAN_COVERAGE]: COLUMNS.DENTAL_PLAN_NAME,
  [COLUMNS.VISION_PLAN_COVERAGE]: COLUMNS.VISION_PLAN_NAME,
};

export { validateValue };
export default VALIDATORS;
